import type { ApartmentAverageForProject } from '@obraplan/core/application/use-cases/takeoff/rate-average-use-cases';
import type { ApartmentAverage } from '@obraplan/core/domain/takeoff/apartment-average';
import { serviceElement } from '@obraplan/core/domain/takeoff/applies-to';
import type { ItemFactor, TypeLineAverage } from '@obraplan/core/domain/takeoff/own-factor';
import type {
  CatalogItem,
  CatalogService,
  ServiceCatalog,
} from '@obraplan/core/domain/takeoff/service-catalog';
import {
  readsSteelRate,
  STEEL_GAUGES,
  type SteelGauge,
  type SteelRate,
  type SteelRateAverage,
} from '@obraplan/core/domain/takeoff/steel';
import type { StructuralElementKind } from '@obraplan/core/domain/takeoff/structural-element';
import { mesmaMedia } from './trazer-a-media';
import {
  fotoDaMedia,
  fotoDaTaxa,
  mediaDaBitola,
  mediaDoItem,
  type MudancaDaTaxa,
} from './trazer-a-media-do-apartamento';

/**
 * O tipo que nasce com a média, e que a segue (Fase 42, etapa 4 — G10, G11; RN-582, RN-583;
 * ADR-191).
 *
 * ```text
 * o card do bloco/pilar/viga: "Tipo" ← P20 (nome novo)
 *   ADD_*_TYPE  ──►  APPLY_*_TYPE_AVERAGE (follow)  ──►  SET_*_TYPE_OF
 *                    └─ cada item dos serviços do elemento com média: o fator médio, MEDIA, foto
 *                    └─ a taxa média de aço do elemento: MEDIA, foto
 * ```
 *
 * O tipo de bloco lê a média da fundação; o de pilar e o de viga, a da estrutura — as duas vêm na
 * mesma resposta (`GetApartmentAverage`), e `mediaDoItem` / `mediaDaBitola` já escolhem a do
 * elemento. A linha "da média" segue a média: não entra na média das outras obras (D5) e recebe
 * "A média mudou"; o número digitado no tipo vira `DIGITADO` (o redutor) e fica, com a média ao
 * lado. Item sem média não ganha linha: a peça do tipo usa o índice da obra nele, como antes.
 *
 * Como o resto da média, é ajuda e é conta pura, para a tela e o teste lerem a mesma lista. O que
 * o serviço aprovado lê não muda (RN-566, RN-570): o tipo novo não ganha linha dele, e o
 * "Atualizar" não o toca.
 */

/** O elemento que tem tipo: o bloco (Fase 41), o pilar e a viga (Fase 42). */
export type ElementoComTipo = 'BLOCO' | StructuralElementKind;

/** Uma linha da composição que a média dá ao tipo, ou que o "Atualizar" troca. */
export interface LinhaDaMediaNoTipo {
  readonly serviceKey: string;
  readonly itemKey: string;
  /** A foto da média: o fator médio, o dia, de quantas obras e peças (`TypeLineAverage`). */
  readonly average: TypeLineAverage;
  /** `true`: a linha passa a ser a média (`MEDIA`). `false`: só a foto muda, o digitado fica. */
  readonly follow: boolean;
}

/** Uma bitola da taxa que a média dá ao tipo, ou que o "Atualizar" troca. */
export interface BitolaDaMediaNoTipo {
  readonly gauge: SteelGauge;
  readonly average: SteelRateAverage;
  /** `true`: a bitola passa a ser a média (`MEDIA`). `false`: só a foto muda. */
  readonly follow: boolean;
}

/** O que a média leva a um tipo: as linhas da composição e as bitolas da taxa (ADR-191). */
export interface MediaNoTipo {
  readonly rates: readonly LinhaDaMediaNoTipo[];
  readonly steelRates: readonly BitolaDaMediaNoTipo[];
}

export const NADA_NO_TIPO: MediaNoTipo = { rates: [], steelRates: [] };

/** O tipo como esta conta o lê: a composição e a taxa, cada linha com a origem e a foto. */
export interface TipoComMedia {
  readonly rates: readonly ItemFactor[];
  readonly steelRates?: readonly SteelRate[];
}

const SEM_TRAVA = (): boolean => false;

/** Três casas, como a taxa (`numeric(12, 3)`). */
const tres = (valor: number): number => Math.round(valor * 1000) / 1000;

/**
 * A foto da linha do tipo, de uma linha da média e do dia da resposta — a do índice
 * (`fotoDaMedia`, seis casas), com as peças no lugar das tipologias: na média da fundação e na da
 * estrutura, `unitTypes` é quantas peças entraram.
 */
export function fotoDaLinhaDoTipo(average: ApartmentAverage, at: string): TypeLineAverage {
  const foto = fotoDaMedia(average, at);
  return { rate: foto.rate, at: foto.at, projects: foto.projects, pieces: foto.unitTypes };
}

/** Os serviços que medem o elemento, e os itens deles que levam fator (o de bitola vai pela taxa). */
function itensDoElemento(
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  elemento: ElementoComTipo,
): readonly { readonly servico: CatalogService; readonly item: CatalogItem }[] {
  return (catalog?.disciplines ?? []).flatMap((disciplina) =>
    disciplina.services.flatMap((servico) =>
      serviceElement(servico) === elemento
        ? servico.items
            .filter((item) => item.steelGauge === undefined)
            .map((item) => ({ servico, item }))
        : [],
    ),
  );
}

/** Algum serviço de aço aprovado lê a taxa do elemento: a do tipo não muda mais (RN-570). */
function acoTravado(
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  elemento: ElementoComTipo,
  travado: (serviceKey: string) => boolean,
): boolean {
  return (catalog?.disciplines ?? []).some((disciplina) =>
    disciplina.services.some(
      (servico) =>
        readsSteelRate(servico) && serviceElement(servico) === elemento && travado(servico.key),
    ),
  );
}

/**
 * O que o tipo novo ganha da média (RN-582): cada item dos serviços que medem o elemento, com
 * média, o fator médio; e a taxa média de aço do elemento, bitola a bitola — tudo a seguir a média
 * (`follow`). Sem média, nada: o tipo nasce vazio e segue a obra, como antes da etapa 4.
 *
 * O serviço aprovado fica de fora (`travado`): dar o tipo à peça mudaria a quantidade dele.
 */
export function mediaDoTipoNovo(
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  elemento: ElementoComTipo,
  media: ApartmentAverageForProject | undefined,
  travado: (serviceKey: string) => boolean = SEM_TRAVA,
): MediaNoTipo {
  if (media?.reference === undefined) return NADA_NO_TIPO;
  const rates = itensDoElemento(catalog, elemento).flatMap(
    ({ servico, item }): LinhaDaMediaNoTipo[] => {
      if (travado(servico.key)) return [];
      const hoje = mediaDoItem(item, servico, media);
      // O fator do tipo é positivo (RN-561): a média zero não vira linha.
      if (hoje === undefined || !(hoje.rate > 0)) return [];
      return [
        {
          serviceKey: servico.key,
          itemKey: item.key,
          average: fotoDaLinhaDoTipo(hoje, media.at),
          follow: true,
        },
      ];
    },
  );
  const steelRates = acoTravado(catalog, elemento, travado)
    ? []
    : STEEL_GAUGES.flatMap(({ code }): BitolaDaMediaNoTipo[] => {
        const hoje = mediaDaBitola(code, media, elemento);
        if (hoje === undefined || !(tres(hoje.rate) > 0)) return [];
        return [{ gauge: code, average: fotoDaTaxa(hoje, media.at), follow: true }];
      });
  return { rates, steelRates };
}

/** Uma linha da composição do tipo que segue a média e que a média de hoje mudou (RN-583). */
export interface MudancaDaLinhaDoTipo {
  readonly serviceKey: string;
  readonly itemKey: string;
  /** "CONCRETAGEM DO PILAR". */
  readonly servico: string;
  readonly descricao: string;
  readonly de: number;
  readonly para: number;
  /** O dia da foto que mudou, `aaaa-mm-dd`. */
  readonly desde?: string;
}

/** O que mudou no tipo: as linhas da composição e as bitolas da taxa. */
export interface MudancasDoTipo {
  readonly fatores: readonly MudancaDaLinhaDoTipo[];
  readonly taxas: readonly MudancaDaTaxa[];
}

/** O item do catálogo de uma linha do tipo, pela chave do serviço e a do item. */
function doCatalogo(
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  elemento: ElementoComTipo,
  linha: Pick<ItemFactor, 'serviceKey' | 'itemKey'>,
): { readonly servico: CatalogService; readonly item: CatalogItem } | undefined {
  return itensDoElemento(catalog, elemento).find(
    ({ servico, item }) => servico.key === linha.serviceKey && item.key === linha.itemKey,
  );
}

/**
 * O que segue a média no tipo e mudou (RN-583): a linha da composição até a quarta casa — a que o
 * card mostra —, a bitola até a terceira. O digitado não entra: ele não muda com a média. O item
 * que a média de hoje não tem também não — a foto continua valendo. O serviço aprovado, nunca.
 */
export function mudancasDoTipo(
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  elemento: ElementoComTipo,
  tipo: TipoComMedia,
  media: ApartmentAverageForProject | undefined,
  travado: (serviceKey: string) => boolean = SEM_TRAVA,
): MudancasDoTipo {
  if (media?.reference === undefined) return { fatores: [], taxas: [] };
  const fatores = tipo.rates.flatMap((linha): MudancaDaLinhaDoTipo[] => {
    if (linha.origin !== 'MEDIA') return [];
    const doItem = doCatalogo(catalog, elemento, linha);
    if (doItem === undefined || travado(doItem.servico.key)) return [];
    const hoje = mediaDoItem(doItem.item, doItem.servico, media);
    if (hoje === undefined || !(hoje.rate > 0) || mesmaMedia(hoje.rate, linha.factor)) return [];
    return [
      {
        serviceKey: linha.serviceKey,
        itemKey: linha.itemKey,
        servico: doItem.servico.name,
        descricao: doItem.item.description,
        de: linha.factor,
        para: fotoDaLinhaDoTipo(hoje, media.at).rate,
        ...(linha.typeAverage === undefined ? {} : { desde: linha.typeAverage.at }),
      },
    ];
  });
  const taxas = acoTravado(catalog, elemento, travado)
    ? []
    : (tipo.steelRates ?? []).flatMap((taxa): MudancaDaTaxa[] => {
        if (taxa.origin !== 'MEDIA') return [];
        const hoje = mediaDaBitola(taxa.gauge, media, elemento);
        if (hoje === undefined || !(tres(hoje.rate) > 0)) return [];
        if (tres(hoje.rate) === tres(taxa.kgPerM3)) return [];
        return [
          {
            gauge: taxa.gauge,
            de: taxa.kgPerM3,
            para: tres(hoje.rate),
            ...(taxa.average === undefined ? {} : { desde: taxa.average.at }),
          },
        ];
      });
  return { fatores, taxas };
}

/**
 * O "Atualizar pela média" do tipo (RN-583, D8): a linha e a bitola que seguem a média passam à
 * de hoje; a digitada ganha a média nova ao lado, sem mudar. A linha que o tipo não tem não nasce
 * aqui — o "Atualizar" troca, não acrescenta.
 */
export function atualizarTipoPelaMedia(
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  elemento: ElementoComTipo,
  tipo: TipoComMedia,
  media: ApartmentAverageForProject | undefined,
  travado: (serviceKey: string) => boolean = SEM_TRAVA,
): MediaNoTipo {
  if (media?.reference === undefined) return NADA_NO_TIPO;
  const rates = tipo.rates.flatMap((linha): LinhaDaMediaNoTipo[] => {
    const doItem = doCatalogo(catalog, elemento, linha);
    if (doItem === undefined || travado(doItem.servico.key)) return [];
    const hoje = mediaDoItem(doItem.item, doItem.servico, media);
    if (hoje === undefined || !(hoje.rate > 0)) return [];
    const segue = linha.origin === 'MEDIA';
    if (segue && mesmaMedia(hoje.rate, linha.factor)) return [];
    return [
      {
        serviceKey: linha.serviceKey,
        itemKey: linha.itemKey,
        average: fotoDaLinhaDoTipo(hoje, media.at),
        follow: segue,
      },
    ];
  });
  const steelRates = acoTravado(catalog, elemento, travado)
    ? []
    : (tipo.steelRates ?? []).flatMap((taxa): BitolaDaMediaNoTipo[] => {
        const hoje = mediaDaBitola(taxa.gauge, media, elemento);
        if (hoje === undefined || !(tres(hoje.rate) > 0)) return [];
        const segue = taxa.origin === 'MEDIA';
        if (segue && tres(hoje.rate) === tres(taxa.kgPerM3)) return [];
        return [{ gauge: taxa.gauge, average: fotoDaTaxa(hoje, media.at), follow: segue }];
      });
  return { rates, steelRates };
}

/** Nada a levar ao tipo: nem linha, nem bitola. */
export function semNadaNoTipo(media: MediaNoTipo): boolean {
  return media.rates.length === 0 && media.steelRates.length === 0;
}
